import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { isApiErrorResponse } from 'ts/common/common';
import { useTranslate } from 'ts/common/hooks';
import { useClientBookingExperience } from 'client_react/booking/ClientBookingExperience';
import { ROUTE } from 'client_react/booking/common';

interface BookingFlowStep {
    route: (typeof ROUTE)[keyof typeof ROUTE];
    isDisabled?: boolean;
    runPrerequisites?: () => Promise<boolean>;
}

const useBookingFlowSteps = () => {
    const {
        bookingSession,
        bookingSessionType,
        contract,
        createContract,
        isPending,
        navigateWithSession,
        questionnaire,
        signatureData,
        signContract,
        submitPayment,
        submitQuestionnaire,
        updateBookingSession
    } = useClientBookingExperience();

    const t = useTranslate('booking');

    const errorToast = useToast({
        title: t('apiError'),
        status: 'error'
    });

    const bookingFlowSteps: Array<BookingFlowStep> = useMemo(
        () => [
            {
                route: ROUTE.DATE_AND_TIME
            },
            {
                route: ROUTE.CONTACT_INFORMATION
            },
            {
                route: ROUTE.QUESTIONNAIRE,
                isDisabled: !bookingSession?.questionnairePublicId
            },
            {
                route: ROUTE.CONTRACT,
                isDisabled: !bookingSessionType?.contractTemplateId,
                runPrerequisites: async () => {
                    const contractResponse = await createContract({
                        data: {
                            contactId: bookingSession?.contactId,
                            contractTemplateId: bookingSessionType?.contractTemplateId,
                            expirationDate: bookingSession?.startTime,
                            name: `${bookingSessionType?.contractTemplateName}`,
                            shootDate: bookingSession?.startTime,
                            type: 'contract'
                        }
                    });

                    if (!contractResponse || isApiErrorResponse(contractResponse)) {
                        errorToast();
                        return false;
                    }

                    const patchContractIdResponse = await updateBookingSession({
                        contractPublicId: contractResponse.publicId,
                        sessionTypePublicId: bookingSessionType?.publicId
                    });

                    if (!patchContractIdResponse || isApiErrorResponse(patchContractIdResponse)) {
                        errorToast();
                        return false;
                    }

                    return true;
                }
            },
            {
                route: ROUTE.PAYMENT,
                isDisabled: bookingSession?.stripePrice === 0
            },
            {
                route: ROUTE.CONFIRMATION,
                runPrerequisites: async () => {
                    if (questionnaire) {
                        const submitQuestionnaireResponse = await submitQuestionnaire({
                            showSuccessToast: false
                        });

                        if (
                            !submitQuestionnaireResponse ||
                            isApiErrorResponse(submitQuestionnaireResponse)
                        ) {
                            return false;
                        }
                    }

                    if (signatureData && contract) {
                        const contractSignatureResponse = await signContract({
                            data: {
                                email: contract.contacts[0].email,
                                signaturePaths: signatureData.signaturePaths,
                                svgViewbox: signatureData.svgViewbox,
                                state: contract.state,
                                type: 'contract-signature'
                            }
                        });

                        if (
                            !contractSignatureResponse ||
                            isApiErrorResponse(contractSignatureResponse)
                        ) {
                            errorToast();
                            return false;
                        }
                    }

                    if (bookingSession?.stripePrice === 0) {
                        const submitPaymentResponse = await submitPayment({
                            paymentAmountType: 'full'
                        });

                        if (!submitPaymentResponse || isApiErrorResponse(submitPaymentResponse)) {
                            errorToast();
                            return false;
                        }
                    }

                    return true;
                }
            }
        ],
        [
            bookingSession,
            bookingSessionType,
            contract,
            createContract,
            errorToast,
            questionnaire,
            signatureData,
            signContract,
            submitPayment,
            submitQuestionnaire,
            updateBookingSession
        ]
    );

    const location = useLocation();

    let currentStepIndex = bookingFlowSteps.findIndex(
        (step) =>
            location.pathname.split('/').filter(Boolean).length < 3 // The third part of the path is the step's route
                ? !step.isDisabled // So, if the path is shorter than 3 parts, the current step is the first step that is not disabled
                : step.route === location.pathname.split('/').pop() // Otherwise, the current step's route is the last part of the path
    );

    if (currentStepIndex === -1) {
        currentStepIndex = 0;
    }

    const goToPreviousStep = useCallback(() => {
        // The previous step is the first step before the current one that is not disabled:
        const previousStep = bookingFlowSteps
            .slice(0, currentStepIndex)
            .reverse()
            .find((step) => !step.isDisabled);

        if (previousStep) {
            navigateWithSession(previousStep.route);
        }
    }, [bookingFlowSteps, currentStepIndex, navigateWithSession]);

    const [shouldGoToNextStep, setShouldGoToNextStep] = useState(false);
    const isInitializedRef = useRef(false);

    const goToNextStep = useCallback(() => {
        setShouldGoToNextStep(true);
    }, []);

    useEffect(() => {
        if (!shouldGoToNextStep) {
            return;
        }

        setShouldGoToNextStep(false);

        // The next step is the first step after the current one that is not disabled:
        const nextStep = bookingFlowSteps
            .slice(currentStepIndex + 1)
            .find((step) => !step.isDisabled);

        (async () => {
            if (nextStep) {
                if (nextStep.runPrerequisites) {
                    const shouldContinue = await nextStep.runPrerequisites();

                    if (!shouldContinue) {
                        return;
                    }
                }

                navigateWithSession(nextStep.route);
            }
        })();
    }, [
        bookingFlowSteps,
        bookingSession,
        currentStepIndex,
        navigateWithSession,
        shouldGoToNextStep
    ]);

    useEffect(() => {
        if (isPending || isInitializedRef.current) {
            return;
        }

        if (!bookingSession && bookingFlowSteps[currentStepIndex].route !== ROUTE.DATE_AND_TIME) {
            navigateWithSession(ROUTE.DATE_AND_TIME);
        } else if (
            bookingSession &&
            !bookingSession.isIncomplete &&
            bookingFlowSteps[currentStepIndex].route !== ROUTE.CONFIRMATION
        ) {
            navigateWithSession(ROUTE.CONFIRMATION);
        }

        isInitializedRef.current = true;
    }, [bookingFlowSteps, bookingSession, currentStepIndex, isPending, navigateWithSession]);

    return {
        goToPreviousStep,
        goToNextStep
    };
};

export default useBookingFlowSteps;
